import React, {FC, useState} from 'react';

import {RoundButton, Tooltip} from '@optimaxdev/design-system';
import {Exit, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';

import {QuoteItemV2} from 'api/magento/magentoAPI.schemas';
import {miniCartDrawerEvent} from 'desktopMiniCart/analytic/analytic';
import {useRemoveCartItem} from 'desktopMiniCart/hooks/useRemoveCartItem/useRemoveCartItem';

import s from './removeItemButton.scss';

export type PropsType = {
    item: QuoteItemV2;
    doesRemoveWithAccessory?: boolean;
    isMiniCart?: boolean;
};

/**
 * Component for render cart item remove button with tooltip
 */
export const RemoveItemButton: FC<PropsType> = ({
    doesRemoveWithAccessory = false,
    isMiniCart = false,
    item,
}) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const {isLoading, removeCartItem} = useRemoveCartItem();

    const handleRemoveClick = () => {
        setIsTooltipOpen(true);
    };

    const toggleTooltip = (isVisible: boolean) => {
        setIsTooltipOpen(isVisible);
    };

    const handleRemoveCartItem = () => {
        removeCartItem(item.id);
        if (isMiniCart) {
            miniCartDrawerEvent('Remove item');
        }
    };

    return (
        <div
            aria-label="Remove item from cart"
            className={classNames(s.exitIconWrapper, {[s.exitIconWrapperMiniCart]: isMiniCart})}
        >
            <Tooltip
                content={
                    <div className={s.tooltipContent}>
                        <p className={s.tooltipText}>
                            Are you sure you want to remove <br />
                            <b className={s.boldText}>{item.windowName}?</b>
                        </p>
                        {doesRemoveWithAccessory && (
                            <p className={classNames(s.tooltipText, s.message)}>
                                The accessories you’ve added <br /> will also be removed.
                            </p>
                        )}
                        <div className={s.tooltipButtonRow}>
                            <div className={s.tooltipButtonWrapper}>
                                <RoundButton
                                    colorName="codGray"
                                    isDisabled={isLoading}
                                    onClick={() => setIsTooltipOpen(false)}
                                    size="extraSmall"
                                    variant="ghost"
                                >
                                    Cancel
                                </RoundButton>
                            </div>
                            <div className={s.tooltipButtonWrapper}>
                                <RoundButton
                                    colorName="codGray"
                                    isDisabled={isLoading}
                                    onClick={handleRemoveCartItem}
                                    size="extraSmall"
                                >
                                    Remove
                                </RoundButton>
                            </div>
                        </div>
                    </div>
                }
                containerClassName={s.tooltip}
                elevation={2}
                isOpen={isTooltipOpen}
                onChange={toggleTooltip}
                position="left"
                renderAs="button"
                windowBorderPadding={16}
            >
                <Icon
                    className={classNames(s.exitIcon, {[s.exitIconMiniCart]: isMiniCart})}
                    icon={Exit}
                    onClick={handleRemoveClick}
                />
            </Tooltip>
        </div>
    );
};
